import type { ComponentProps } from 'react'
import type {
  Product,
  ProductIndexFilters,
  ProductStorefrontVisibility as StorefrontVisibility,
} from '@/lib/services/products/products.types'

import SearchInput from '@/components/form/SearchInput'
import { EmptyState } from '@/components/patterns/empty-state'
import { LoadingState } from '@/components/patterns/loading-state'
import { MediaPlaceholder } from '@/components/patterns/media-placeholder'
import ProductStatusIcon from '@/components/products/ProductStatusIcon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { ProductStatus } from '@/lib/enums/ProductStatus'
import { ProductStorefrontVisibility } from '@/lib/enums/ProductStorefrontVisibility'
import { useProducts } from '@/lib/services/products/products.query'
import { getErrorMessage } from '@/lib/utils'
import { useOptionalRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { useDebounce } from '@uidotdev/usehooks'
import {
  Eye,
  EyeOff,
  FolderTree,
  Loader2,
  PackageSearch,
  RefreshCw,
  SlidersHorizontal,
} from 'lucide-react'
import { useState } from 'react'

const PAGE_SIZE = 20
const SEARCH_DEBOUNCE_MS = 300
const ALL_VISIBILITIES = 'all'

const visibilityOptions = [
  {
    value: ALL_VISIBILITIES,
    label: 'All website visibility',
    icon: SlidersHorizontal,
  },
  {
    value: ProductStorefrontVisibility.Always.Value,
    label: 'Always visible',
    icon: Eye,
  },
  {
    value: ProductStorefrontVisibility.PublishedCollection.Value,
    label: 'Only in visible collections',
    icon: FolderTree,
  },
  {
    value: ProductStorefrontVisibility.Hidden.Value,
    label: 'Hidden from website',
    icon: EyeOff,
  },
] satisfies {
  value: StorefrontVisibility | typeof ALL_VISIBILITIES
  label: string
  icon: typeof Eye
}[]

function productId(product: Pick<Product, 'id'> | null | undefined) {
  return Number(product?.id)
}

function isHiddenFromWebsite(
  product: Pick<Product, 'status' | 'storefront_visibility'> | null | undefined
) {
  return (
    product?.status !== ProductStatus.Active.Value ||
    product?.storefront_visibility === ProductStorefrontVisibility.Hidden.Value
  )
}

function isIneligibleCandidate(
  product: Pick<Product, 'status' | 'is_add_on_only'> | null | undefined
) {
  return product?.status === ProductStatus.Archived.Value || product?.is_add_on_only === true
}

function ProductRow({
  product,
  checked,
  onCheckedChange,
  selectedView = false,
  disabled = false,
}: {
  product: Product
  checked: boolean
  onCheckedChange: ComponentProps<typeof Checkbox>['onCheckedChange']
  selectedView?: boolean
  disabled?: boolean
}) {
  const id = productId(product)
  const inputId = `${selectedView ? 'selected' : 'available'}-collection-product-${id}`
  const media = product?.media?.[0]

  return (
    <li className="border-b border-border/60 last:border-b-0">
      <Label htmlFor={inputId} appearance="choiceListRow">
        <Checkbox
          id={inputId}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onCheckedChange}
          aria-label={`Select ${product?.name ?? 'product'}`}
        />
        {media?.url ? (
          <div className="size-14 shrink-0 overflow-hidden rounded-xl border border-border/60 bg-muted/10">
            <img
              src={media.url}
              alt=""
              className="block size-full object-cover object-center"
              loading="lazy"
            />
          </div>
        ) : (
          <MediaPlaceholder size="sm" />
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <Typography variant="labelStrong" className="min-w-0 truncate">
              {product?.name}
            </Typography>
            <ProductStatusIcon product={product} />
          </div>
          {selectedView && isIneligibleCandidate(product) ? (
            <Typography variant="caption" tone="warning">
              No longer available to add
            </Typography>
          ) : null}
        </div>
      </Label>
    </li>
  )
}

function PickerPagination({
  currentPage,
  lastPage,
  onPageChange,
  disabled = false,
}: {
  currentPage: number
  lastPage: number
  onPageChange: (page: number) => void
  disabled?: boolean
}) {
  if (lastPage <= 1) {
    return null
  }

  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border/60 bg-card px-3 py-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
      >
        Previous
      </Button>
      <Typography variant="caption" aria-live="polite">
        Page {currentPage} of {lastPage}
      </Typography>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || currentPage >= lastPage}
        onClick={() => onPageChange(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  )
}

const CollectionProductPicker = ({
  initialData = [],
  onConfirm,
  onCancel,
}: {
  initialData?: Product[]
  onConfirm?: (products: Product[]) => void
  onCancel?: () => void
}) => {
  const [search, setSearch] = useState('')
  const [visibility, setVisibility] = useState<StorefrontVisibility | typeof ALL_VISIBILITIES>(
    ALL_VISIBILITIES
  )
  const [page, setPage] = useState(1)
  const [selectedPage, setSelectedPage] = useState(1)
  const [selected, setSelected] = useState(
    () => new Map(initialData.map((product) => [productId(product), product]))
  )
  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS)
  const organizationId = useOptionalRouteOrganizationId()

  const filters = {
    include: ['media'],
    exclude_add_on_only: true,
    exclude_archived: true,
    page,
    page_size: PAGE_SIZE,
    sort: debouncedSearch ? 'relevance' : 'name_asc',
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(visibility !== ALL_VISIBILITIES ? { effective_storefront_visibility: visibility } : {}),
  } satisfies ProductIndexFilters
  const { data, error, isError, isFetching, isPending, refetch } = useProducts({
    organizationId,
    filters,
    params: { throwOnError: false },
  })

  const pageData = Array.isArray(data) ? undefined : data
  const products = pageData?.data ?? []
  const isUpdatingResults = search.trim() !== debouncedSearch || isFetching
  const currentPage = Number(pageData?.meta?.current_page ?? page)
  const lastPage = Math.max(1, Number(pageData?.meta?.last_page ?? 1))
  const resultCount = Number(pageData?.meta?.total ?? products.length)
  const selectedProducts = Array.from(selected.values())
  const hiddenCount = selectedProducts.filter(isHiddenFromWebsite).length
  const selectedLastPage = Math.max(1, Math.ceil(selectedProducts.length / PAGE_SIZE))
  const visibleSelectedPage = Math.min(selectedPage, selectedLastPage)
  const selectedPageProducts = selectedProducts.slice(
    (visibleSelectedPage - 1) * PAGE_SIZE,
    visibleSelectedPage * PAGE_SIZE
  )
  const selectedOnPage = products.filter((product) => selected.has(productId(product))).length
  const pageSelectionState =
    products.length > 0 && selectedOnPage === products.length
      ? true
      : selectedOnPage > 0
        ? 'indeterminate'
        : false

  const updateSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const updateSelection = (
    product: Product,
    isChecked: ComponentProps<typeof Checkbox>['checked']
  ) => {
    setSelected((previous) => {
      const next = new Map(previous)
      const id = productId(product)

      if (isChecked) {
        next.set(id, product)
      } else {
        next.delete(id)
      }

      return next
    })
  }

  const updateCurrentPageSelection = (isChecked: ComponentProps<typeof Checkbox>['checked']) => {
    setSelected((previous) => {
      const next = new Map(previous)

      products.forEach((product) => {
        const id = productId(product)
        if (isChecked) {
          next.set(id, product)
        } else {
          next.delete(id)
        }
      })

      return next
    })
  }

  const applySelection = () => {
    const initialIds = new Set(initialData.map(productId))
    const newlyAdded = selectedProducts.filter((product) => !initialIds.has(productId(product)))
    const retained = initialData.filter((product) => selected.has(productId(product)))

    onConfirm?.([...newlyAdded, ...retained])
  }

  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-4">
      <Tabs defaultValue="all" className="min-h-0 flex-1">
        <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
          <TabsList>
            <TabsTrigger value="all">All products</TabsTrigger>
            <TabsTrigger value="selected">Selected ({selected.size})</TabsTrigger>
          </TabsList>
          {hiddenCount > 0 ? (
            <div className="flex items-center justify-end pb-2" aria-live="polite">
              <Badge variant="warning" size="count">
                {hiddenCount} hidden
              </Badge>
            </div>
          ) : null}
        </div>

        <TabsContent value="all" className="flex min-h-0 flex-col gap-3">
          <div className="grid gap-3 @2xl:grid-cols-[minmax(0,1fr)_auto]">
            <div className="space-y-1">
              <SearchInput
                aria-label="Search products"
                placeholder="Search products"
                value={search}
                setValue={updateSearch}
              />
              <Typography variant="caption">
                Search by name, flower, color, occasion, or product type.
              </Typography>
            </div>
            <Select
              value={visibility}
              onValueChange={(value) => {
                const option = visibilityOptions.find((candidate) => candidate.value === value)
                if (!option) return
                setVisibility(option.value)
                setPage(1)
              }}
            >
              <SelectTrigger
                aria-label="Filter by website visibility"
                className="w-full @2xl:w-[15rem]"
                selected={visibility !== ALL_VISIBILITIES}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {visibilityOptions.map((option) => {
                  const Icon = option.icon

                  return (
                    <SelectItem key={option.value} value={option.value}>
                      <Icon className="size-4" aria-hidden="true" />
                      {option.label}
                    </SelectItem>
                  )
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="flex min-h-8 flex-wrap items-center gap-3">
            <Label htmlFor="select-collection-products-page" appearance="choice" weight="normal">
              <Checkbox
                id="select-collection-products-page"
                checked={pageSelectionState}
                disabled={products.length === 0 || isUpdatingResults}
                onCheckedChange={updateCurrentPageSelection}
              />
              <span>{pageSelectionState === true ? 'Deselect this page' : 'Select this page'}</span>
            </Label>
            <Typography variant="supporting" className="ml-auto">
              {resultCount} result{resultCount === 1 ? '' : 's'}
            </Typography>
            {isUpdatingResults && !isPending ? (
              <Typography
                variant="caption"
                className="inline-flex items-center gap-1.5"
                role="status"
              >
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                Updating results…
              </Typography>
            ) : null}
          </div>

          <Card
            size="flush"
            className="min-h-0 flex-1 overflow-hidden"
            aria-busy={isUpdatingResults}
          >
            <ScrollArea className="h-[min(28rem,52dvh)] min-h-0 shrink">
              {isPending ? (
                <LoadingState
                  label="Loading products…"
                  size="spacious"
                  surface="transparent"
                  className="h-full rounded-none border-0"
                />
              ) : isError && products.length === 0 ? (
                <EmptyState
                  icon={RefreshCw}
                  title="Products could not be loaded"
                  description={getErrorMessage(error)}
                  size="spacious"
                  surface="transparent"
                  className="h-full rounded-none border-0"
                  action={
                    <Button type="button" variant="outline" size="sm" onClick={() => refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : products.length === 0 ? (
                <EmptyState
                  icon={PackageSearch}
                  title="No products found"
                  description="Try a different search or website visibility filter."
                  size="spacious"
                  surface="transparent"
                  className="h-full rounded-none border-0"
                />
              ) : (
                <ul>
                  {products.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      checked={selected.has(productId(product))}
                      disabled={isUpdatingResults}
                      onCheckedChange={(isChecked) => updateSelection(product, isChecked)}
                    />
                  ))}
                </ul>
              )}
            </ScrollArea>
            {isError && products.length > 0 ? (
              <div className="flex flex-wrap items-center gap-2 border-t border-status-warning-border bg-status-warning-background px-3 py-2">
                <Typography variant="caption" tone="warning">
                  Results could not be refreshed: {getErrorMessage(error)}
                </Typography>
                <Button type="button" variant="outline" size="xs" onClick={() => refetch()}>
                  Try again
                </Button>
              </div>
            ) : null}
            <PickerPagination
              currentPage={currentPage}
              lastPage={lastPage}
              disabled={isUpdatingResults}
              onPageChange={setPage}
            />
          </Card>
        </TabsContent>

        <TabsContent value="selected" className="flex min-h-0 flex-col">
          <Card size="flush" className="min-h-0 flex-1 overflow-hidden">
            <ScrollArea className="h-[min(28rem,52dvh)] min-h-0 shrink">
              {selectedPageProducts.length === 0 ? (
                <EmptyState
                  icon={PackageSearch}
                  title="No products selected"
                  description="Choose products from the All products tab."
                  size="spacious"
                  surface="transparent"
                  className="h-full rounded-none border-0"
                />
              ) : (
                <ul>
                  {selectedPageProducts.map((product) => (
                    <ProductRow
                      key={product.id}
                      product={product}
                      checked
                      selectedView
                      onCheckedChange={(isChecked) => updateSelection(product, isChecked)}
                    />
                  ))}
                </ul>
              )}
            </ScrollArea>
            <PickerPagination
              currentPage={visibleSelectedPage}
              lastPage={selectedLastPage}
              onPageChange={setSelectedPage}
            />
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-border/60 bg-card pt-4">
        <Button type="button" variant="outline" onClick={() => onCancel?.()}>
          Cancel
        </Button>
        <Button type="button" onClick={applySelection}>
          Apply products
        </Button>
      </div>
    </div>
  )
}

export { PAGE_SIZE, SEARCH_DEBOUNCE_MS }
export default CollectionProductPicker
